import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { RequestMethod } from '@angular/http';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/retryWhen';
import 'rxjs/add/operator/scan';
import 'rxjs/add/operator/takeWhile';
import 'rxjs/add/operator/timeout';
import { Observable } from 'rxjs/Observable';
import { IAppConfig } from '../../store/interfaces/IAppConfig';
import { VALIDATION_ERROR_CODE } from '../model/errors/service-error.model';
import { LogService } from '../services/logging.service';
import { RetryHandler } from './handlers/retry.handlers';
import { DataValidator } from './validators/data.validators';

export abstract class GenericDao {
  protected name: string;
  private http: HttpClient;
  protected appConfig: IAppConfig;
  protected daoConfig: any;
  protected retryConfig: any;
  protected log: LogService;

  private dataValidator: DataValidator;
  private retryHandler: RetryHandler;

  constructor(name: string, http: HttpClient, appConfig: IAppConfig, daoConfig: any, retryHandler: RetryHandler, dataValidator: DataValidator, log: LogService) {
    this.name = name;
    this.http = http;
    this.appConfig = appConfig;
    this.daoConfig = daoConfig;
    this.retryHandler = retryHandler;
    this.dataValidator = dataValidator;
    this.log = log;
    this.setRetry(this.daoConfig);
  }

  protected doGet(url, params = {}, headers?: { [key: string]: string }, withCredentials?: boolean): Observable<any> {
    return this.http.get(url, {headers: this.buildHeaders(headers), params: <HttpParams> params, withCredentials: !!withCredentials})
      .retryWhen((error) => this.handleRetry(error, RequestMethod.Get, url, params))
      .map(response => this.dataValidator.validate(response, RequestMethod.Get))
      .catch((error) => {
        this._logError(`Error during GET [${url}] with status code ${error.status}`, error, params);
        return Observable.throw(error);
      });
  };

  protected doDelete(url, headers?: { [key: string]: string }): Observable<any> {
    return this.http.delete(url, {headers: this.buildHeaders(headers)})
      .retryWhen((error) => this.handleRetry(error, RequestMethod.Delete, url))
      .map(response => this.dataValidator.validate(response, RequestMethod.Delete))
      .catch((error) => {
        this._logError(`Error during DELETE [${url}] with status code ${error.status}`, error);
        return Observable.throw(error);
      });
  };

  protected doPut(url, body: any, headers?: { [key: string]: string }, params?: {}): Observable<any> {
    return this.http.put(url, body, {headers: this.buildHeaders(headers), params: <HttpParams> params})
      .retryWhen((errors) => this.handleRetry(errors, RequestMethod.Put, url, body))
      .map(response => this.dataValidator.validate(response, RequestMethod.Put))
      .catch((error) => {
        this._logError(`Error during PUT. URL: [${url}] with status code ${error.status}`, error, body);
        return Observable.throw(error);
      });
  };

  protected doPost(url, body: any, headers?: { [key: string]: string }): Observable<any> {
    return this.http.post(url, body, {headers: this.buildHeaders(headers)})
      .retryWhen((error) => this.handleRetry(error, RequestMethod.Post, url, body))
      .map(response => this.dataValidator.validate(response, RequestMethod.Post))
      .catch((error) => {
        this._logError(`Error during POST. URL: [${url}] with status code ${error.status}`, error, body);
        return Observable.throw(error);
      });
  };

  protected doPatch(url, body: any = '', headers?: { [key: string]: string }): Observable<any> {
    return this.http.patch(url, body, {headers: this.buildHeaders(headers)})
      .retryWhen((error) => this.handleRetry(error, RequestMethod.Post, url, body))
      .map(response => this.dataValidator.validate(response, RequestMethod.Patch))
      .catch((error) => {
        this._logError(`Error during PATCH. URL: [${url}] with status code ${error.status}`, error, body);
        return Observable.throw(error);
      });
  };

  protected handleRetry(errors: Observable<any>, method: RequestMethod, url: string, request?: any): Observable<any> {
    return errors.scan((retryCount, error) => {
      this.log.warn(`Error during try # ${retryCount} of ${this.retryConfig.maxRetries} in ${this.name} during ${RequestMethod[method]} [${url}]`, this.name, error, request);
      ++retryCount;
      return this.retryHandler.handle(error, method, retryCount, this.retryConfig);
    }, 0)
      .takeWhile(count => count <= (this.retryConfig.maxRetries + 1));
  }

  protected setRetry(daoConfig: any) {
    this.retryConfig = {};
    this.retryConfig.delay = daoConfig.interval || 0;
    this.retryConfig.timeout = daoConfig.timeout || 1500;
    this.retryConfig.maxRetries = daoConfig.retry || 0;
  }

  protected buildHeaders(customHeaders?: { [key: string]: string }): HttpHeaders {
    const headers = {};
    if (this.retryConfig.timeout) {
      headers['timeout'] = String(this.retryConfig.timeout);
    }
    return new HttpHeaders(Object.assign({}, headers, customHeaders));
  }

  private _logError(message: string, error: any, details?: any) {
    if (error && error.status === VALIDATION_ERROR_CODE) {
      this.log.warn(message, this.name, error, details);
    } else {
      this.log.error(message, this.name, error, details);
    }
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/daos/generic.dao.ts